import { BidLineItem } from '../models/bid';

/**
 * Calculate the sum of all children's total costs
 */
export const calculateChildrenSum = (item: BidLineItem): number | null => {
  if (!item.children || item.children.length === 0) return null;

  const childrenWithCosts = item.children.filter(child => child.totalCost !== null && child.totalCost !== undefined);
  if (childrenWithCosts.length === 0) return null;

  return childrenWithCosts.reduce((sum, child) => sum + (child.totalCost || 0), 0);
};

/**
 * Check if there's a parent-child sum mismatch
 */
export const hasParentChildMismatch = (item: BidLineItem, threshold: number = 1): boolean => {
  if (!item.totalCost) return false;

  const childrenSum = calculateChildrenSum(item);
  if (childrenSum === null) return false;

  return Math.abs(childrenSum - item.totalCost) > threshold;
};

/**
 * Calculate the variance percentage shown on a line item.
 *
 * The range gates whether we show anything: a quote inside [low, high] is
 * "in range" (returns 0) — Fxer ranges run wide, so being within them is not a
 * finding. Once the quote is OUTSIDE the band (either direction), we measure the
 * distance from the MIDPOINT, so the % reconciles with the midpoint estimate we
 * display ("Est: $X") rather than the range edge.
 *
 * Returns null if not comparable, 0 if in range, negative if under, positive if over.
 */
export const calculateVariance = (
  actual: number | null | undefined,
  low: number,
  high: number
): number | null => {
  if (actual === null || actual === undefined || low === 0 || high === 0) return null;

  // Inside the band → in range, no number.
  if (actual >= low && actual <= high) return 0;

  // Outside the band → distance from the midpoint (reconciles with the displayed estimate).
  const midpoint = (low + high) / 2;
  return Math.round(((actual - midpoint) / midpoint) * 100);
};

/**
 * Get assessment color class based on assessment string
 */
export const getAssessmentColor = (assessment: string | null | undefined): {
  text: string;
  bg: string;
  border: string;
} => {
  if (!assessment) {
    return {
      text: 'text-gray-600',
      bg: 'bg-gray-50',
      border: 'border-gray-200'
    };
  }

  const assessmentLower = assessment.toLowerCase();

  if (assessmentLower === 'reasonable' || assessmentLower === 'fair') {
    return {
      text: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200'
    };
  }

  if (assessmentLower === 'suspiciously_low' || assessmentLower.includes('under')) {
    return {
      text: 'text-amber-600',
      bg: 'bg-amber-50',
      border: 'border-amber-200'
    };
  }

  if (assessmentLower === 'concern' || assessmentLower === 'overpriced' || assessmentLower.includes('over')) {
    return {
      text: 'text-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200'
    };
  }

  return {
    text: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200'
  };
};

/**
 * Get variance display information
 */
export const getVarianceDisplay = (variance: number | null): {
  text: string;
  className: string;
  isSignificant: boolean;
} | null => {
  if (variance === null) return null;

  if (variance === 0) {
    return {
      text: 'In range',
      className: 'text-green-600',
      isSignificant: false
    };
  }

  const isSignificant = Math.abs(variance) > 20;
  const isMedium = Math.abs(variance) > 10;

  return {
    text: variance > 0 ? `${variance}% over` : `${Math.abs(variance)}% under`,
    className: variance > 0
      ? (isSignificant ? 'text-red-600' : (isMedium ? 'text-orange-600' : 'text-gray-600'))
      : (isSignificant ? 'text-amber-600' : 'text-gray-600'),
    isSignificant
  };
};

/**
 * Check if line item has children with costs
 */
export const hasChildrenWithCosts = (item: BidLineItem): boolean => {
  return item.children?.some(child => child.totalCost !== null && child.totalCost !== undefined) || false;
};

/**
 * Check if line item is a "rolled up from children" item
 */
export const isRolledUpFromChildren = (item: BidLineItem): boolean => {
  return item.costAnalysis?.reasoning?.toLowerCase().includes('rolled up from children') || false;
};

/**
 * Format currency with proper handling of null/undefined
 */
export const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format range display
 */
export const formatRange = (low: number, high: number, format: 'currency' | 'number' = 'currency'): string => {
  if (format === 'currency') {
    return `${formatCurrency(low)} - ${formatCurrency(high)}`;
  }
  return `${low.toLocaleString()} - ${high.toLocaleString()}`;
};
